/**
 * AgentsList Page
 * Displays list of all Windows Agents with real-time status
 * Dark theme matching SYLink platform design
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  RefreshCw,
  Trash2,
  Eye,
  Server,
  Activity,
  Clock,
  AlertCircle,
  Download,
  Wifi,
  WifiOff,
  Cpu,
  Monitor
} from 'lucide-react';
import { getAgents, deleteAgent, getAgentsStats, downloadInstaller } from '../../api/agents';
import AgentStatusBadge from '../../components/Agents/AgentStatusBadge';
import RegisterAgentModal from '../../components/Agents/RegisterAgentModal';

const AgentsList = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    busy: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadAgents = async () => {
    try {
      setError(null);
      const [agentsData, statsData] = await Promise.all([
        getAgents().catch(() => []),
        getAgentsStats().catch(() => ({}))
      ]);
      setAgents(Array.isArray(agentsData) ? agentsData : []);
      const agentStats = statsData?.agents || statsData || {};
      setStats({
        total: agentStats.total || 0,
        online: agentStats.online || 0,
        offline: agentStats.offline || 0,
        busy: agentStats.busy || 0
      });
    } catch (err) {
      console.error('Failed to load agents:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des agents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAgents();
    const interval = setInterval(() => { loadAgents(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAgents();
  };

  const handleDelete = async (agentId, agentName) => {
    if (!window.confirm(`Supprimer l'agent "${agentName || 'Sans nom'}" ?`)) return;
    try {
      await deleteAgent(agentId);
      setAgents(prev => prev.filter(a => a.id !== agentId));
      const statsData = await getAgentsStats().catch(() => ({}));
      const agentStats = statsData?.agents || statsData || {};
      setStats({
        total: agentStats.total || 0,
        online: agentStats.online || 0,
        offline: agentStats.offline || 0,
        busy: agentStats.busy || 0
      });
    } catch (err) {
      alert(`Erreur: ${err.response?.data?.detail || err.message}`);
    }
  };

  const filteredAgents = agents.filter(agent => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      agent.name?.toLowerCase().includes(q) ||
      agent.hostname?.toLowerCase().includes(q) ||
      agent.uid?.toLowerCase().includes(q) ||
      agent.internal_ip?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-green mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Chargement des agents...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total', value: stats.total, icon: Server, color: 'brand-green', borderColor: 'border-brand-green/30', iconBg: 'bg-brand-green/10', iconColor: 'text-brand-green' },
    { label: 'En ligne', value: stats.online, icon: Wifi, color: 'emerald', borderColor: 'border-emerald-500/30', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400' },
    { label: 'Hors ligne', value: stats.offline, icon: WifiOff, color: 'gray', borderColor: 'border-gray-500/30', iconBg: 'bg-gray-500/10', iconColor: 'text-gray-400' },
    { label: 'Occupés', value: stats.busy, icon: Cpu, color: 'orange', borderColor: 'border-orange-500/30', iconBg: 'bg-orange-500/10', iconColor: 'text-orange-400' },
  ];

  return (
    <div className="min-h-screen bg-brand-black">
      <div className="w-[95%] max-w-[1400px] mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-lg bg-brand-green/10">
              <Monitor className="w-6 h-6 text-brand-green" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Agents</h1>
              <p className="text-sm text-gray-400">
                Agents de scan déployés sur les réseaux internes
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className={`bg-brand-black-light rounded-lg border ${card.borderColor} px-4 py-3 transition-all duration-300 hover:border-opacity-60`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{card.label}</p>
                    <p className={`text-xl font-bold ${card.iconColor} mt-1`}>{card.value}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${card.iconBg}`}>
                    <Icon className={`w-5 h-5 ${card.iconColor}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions Bar */}
        <div className="bg-brand-black-light rounded-lg border border-gray-800 px-4 py-3 mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 w-full md:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un agent..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-[#060606] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-green/50 focus:ring-1 focus:ring-brand-green/20 transition-colors"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 w-full md:w-auto">
              <button
                onClick={() => downloadInstaller()}
                className="px-3 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-brand-black-lighter hover:border-gray-600 transition-colors flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" />
                <span className="hidden md:inline">Télécharger</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-3 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-brand-black-lighter hover:border-gray-600 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">Actualiser</span>
              </button>
              <button
                onClick={() => setIsRegisterModalOpen(true)}
                className="px-4 py-2 bg-brand-green text-brand-black font-semibold rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Enregistrer
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Content */}
        {filteredAgents.length === 0 ? (
          <div className="bg-brand-black-light rounded-lg border border-gray-800 p-12 text-center">
            <div className="p-4 rounded-full bg-brand-green/10 w-fit mx-auto mb-4">
              <Server className="w-10 h-10 text-brand-green/60" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              {searchQuery ? 'Aucun agent trouvé' : 'Aucun agent enregistré'}
            </h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              {searchQuery
                ? 'Modifiez votre recherche ou enregistrez un nouvel agent'
                : 'Déployez un agent sur un réseau client pour scanner les cibles internes sans VPN'}
            </p>
            {!searchQuery && (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setIsRegisterModalOpen(true)}
                  className="px-4 py-2.5 bg-brand-green text-brand-black font-semibold rounded-lg hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Enregistrer un Agent
                </button>
                <button
                  onClick={() => downloadInstaller()}
                  className="px-4 py-2.5 border border-brand-green/30 text-brand-green rounded-lg hover:bg-brand-green/10 transition-colors inline-flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Télécharger l'installateur
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-brand-black-light rounded-lg border border-gray-800 overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Hostname</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">IP Interne</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Dernière connexion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">Métriques</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {filteredAgents.map((agent) => (
                  <tr
                    key={agent.id}
                    className="hover:bg-brand-black-lighter/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/agents/${agent.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-1.5 rounded-lg ${agent.status === 'online' ? 'bg-brand-green/10' : 'bg-gray-700/30'}`}>
                          <Server className={`w-4 h-4 ${agent.status === 'online' ? 'text-brand-green' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white">
                            {agent.name || agent.hostname || 'Agent'}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">{agent.uid}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <AgentStatusBadge status={agent.status} lastHeartbeat={agent.last_heartbeat} size="sm" />
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-300">{agent.hostname || '-'}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-sm text-gray-300 font-mono">{agent.internal_ip || '-'}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-sm text-gray-400">
                        {agent.last_heartbeat
                          ? new Date(agent.last_heartbeat).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                          : 'Jamais'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      {agent.status === 'online' && agent.cpu_percent != null ? (
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-gray-400">CPU <span className="text-white font-medium">{agent.cpu_percent}%</span></span>
                          <span className="text-gray-400">RAM <span className="text-white font-medium">{agent.memory_percent}%</span></span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/agents/${agent.id}`); }}
                          className="p-1.5 text-gray-400 hover:text-brand-green hover:bg-brand-green/10 rounded-lg transition-colors"
                          title="Détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(agent.id, agent.name); }}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Register Agent Modal */}
      <RegisterAgentModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSuccess={() => loadAgents()}
      />
    </div>
  );
};

export default AgentsList;
